/**
 * ZAR-1762 — Tips & Gratuity KPI (TG-1) + two-section drill-in (TG-2).
 *
 * Single source of truth for everything the unified "Tips & Grat" card and the
 * "Tips & Gratuity" drill-in display, so Snapshot / Sales / EOD Reconciliation all
 * derive the same numbers from the same rules. No JSX lives here.
 *
 * The two pools are NEVER blended: voluntary tips and auto-gratuity keep separate
 * totals, rates and staff rankings (tips are tip income eligible for the FICA tip
 * credit; auto-gratuity is non-tip wages). Only the card face shows their sum.
 */

/** Short label for the KPI card face — "Tips & Gratuity" does not fit a small-KPI slot. */
export const TG_CARD_LABEL = 'Tips & Grat';
/** Label for house-retained outlets, where gratuity is revenue and never reaches staff. */
export const TG_CARD_LABEL_TIPS_ONLY = 'Tips';
/** Breadcrumb / drill-in title keeps the full name. */
export const TG_DRILL_TITLE = 'Tips & Gratuity';

export interface TgStaffRow {
    name: string;
    amount: number;
    /** Individual rate vs that staff member's own sales. null → incalculable, render "—". */
    ratePct: number | null;
    /**
     * REF-6 — part of `amount` that was refunded AFTER this staff member's tip period had
     * already closed (tip-out paid). Undefined/0 → no exposure, no icon.
     */
    refundedAfterTipOut?: number;
}

export interface TgChannelRow {
    channel: string;
    amount: number;
    /** Percent of the section total. The column always sums to exactly 100. */
    pct: number;
}

export interface TgSection {
    total: number;
    /** Average per qualifying order. null → incalculable (no orders), render "—". */
    avg: number | null;
    /** (total / net sales) × 100. */
    ratePct: number;
    /** Count of orders that carried this pool. */
    orders: number;
    staff: TgStaffRow[];
    channels: TgChannelRow[];
    /** Decimal places `channels[].pct` is distributed and rendered at. */
    pctDecimals: number;
}

export interface TipsGratuityData {
    /** Tips + staff-bound gratuity — the only figure the card face shows. */
    combined: number;
    tips: TgSection;
    gratuity: TgSection;
}

/** One staff × channel bucket as returned by /sales/live/v2/staff-perfomance. */
export interface TgStaffInput {
    /** `staff_name` — drives both staff rankings. */
    name: string;
    tip: number;
    gratuity: number;
    orderCount: number;
    netSales: number;
}

/** One channel bucket as returned by /sales/live/v2/by-channel. */
export interface TgChannelInput {
    channel: string | null;
    tip: number;
    gratuity: number;
}

/** Bucket for rows the source endpoint gave no channel for. */
export const TG_CHANNEL_UNASSIGNED = 'Unassigned';

/**
 * Map a channel string to its display bucket.
 *
 * A row with NO channel is "Unassigned", not "3rd Party". The two are very different claims:
 * "3rd Party" says the money came through a marketplace, which a manager will believe and act
 * on — and for gratuity it is almost always the opposite of the truth (auto-gratuity is a
 * dine-in large-party charge). Only a channel string we genuinely don't recognise is treated
 * as a marketplace order.
 */
export const getCanonicalTgChannel = (channel: string | null): string => {
    const raw = (channel ?? '').trim();
    if (!raw) return TG_CHANNEL_UNASSIGNED;
    const key = raw.toLowerCase().replace(/[\s\-_]+/g, '');
    if (/dinein/.test(key)) return 'Dine-In';
    if (/pickup/.test(key)) return 'Pickup';
    if (/delivery/.test(key)) return 'Delivery';
    if (/instore|inhouse/.test(key)) return 'Instore';
    return '3rd Party';
};

/**
 * Largest Remainder Method so the percent column always sums to exactly 100 — at whatever
 * precision the section renders at. Work is done in the smallest displayed unit (whole
 * percent at 0dp, hundredths of a percent at 2dp) so the rounding that reaches the screen is
 * the same rounding that was balanced; distributing at full precision and rounding for
 * display afterwards is what makes columns show 99.99% or 100.01%.
 */
const distributeTg100 = (values: number[], decimals = 0): number[] => {
    const total = values.reduce((s, v) => s + v, 0);
    if (total === 0) return values.map(() => 0);
    const scale = 10 ** decimals;
    const units = 100 * scale;
    const raw = values.map((v) => (v / total) * units);
    const floored = raw.map((p) => Math.floor(p));
    let remainder = units - floored.reduce((s, v) => s + v, 0);
    const order = raw
        .map((p, i) => ({ i, frac: p - Math.floor(p) }))
        .sort((a, b) => b.frac - a.frac);
    for (const { i } of order) {
        if (remainder <= 0) break;
        floored[i] += 1;
        remainder -= 1;
    }
    return floored.map((u) => u / scale);
};

/**
 * Combined delta for the card face — fallback for surfaces whose comparison payload has
 * no combined figure of its own.
 *
 * Snapshot and Sales do NOT use this: `/sales/live/v2/snapshot-details/comparison` returns
 * `tipAndGratuity` directly and they read that. Only EOD Reconciliation, whose summary
 * reports tip% and gratuity% separately, still needs this — the prior-period amounts are
 * back-solved (prior = current / (1 + pct/100)) and re-compared as one total. A pct of
 * exactly -100 (or lower) makes the prior amount unrecoverable, so that pool is dropped
 * rather than guessed; if neither pool is recoverable, or the prior total is zero, there is
 * no delta to show.
 */
export const combineTgDeltaPct = (
    tips: number,
    tipsPct: number | null | undefined,
    gratuity: number,
    gratuityPct: number | null | undefined,
): number | null => {
    const prior = (current: number, pct: number | null | undefined): number | null => {
        if (pct == null || !Number.isFinite(pct)) return null;
        const factor = 1 + pct / 100;
        if (factor <= 0) return null;
        return current / factor;
    };
    const priorTips = prior(tips, tipsPct);
    const priorGratuity = prior(gratuity, gratuityPct);
    if (priorTips == null && priorGratuity == null) return null;
    const priorTotal = (priorTips ?? 0) + (priorGratuity ?? 0);
    if (priorTotal <= 0) return null;
    return (((tips + gratuity) - priorTotal) / priorTotal) * 100;
};

const buildSection = (
    total: number,
    /** Count shown as "Orders w/ Tips" / "Orders w/ Gratuity". */
    orders: number,
    /**
     * Base the section's average divides by. Deliberately separate from `orders`: the Tips
     * section averages over TOTAL orders, the Gratuity section over gratuity-bearing orders
     * only (TG-2 acceptance criteria).
     */
    avgBase: number,
    netSales: number,
    staff: TgStaffRow[],
    channels: TgChannelRow[],
    pctDecimals: number,
): TgSection => ({
    total,
    // "—" rather than $0.00 when there is no base to divide by (no divide-by-zero).
    avg: avgBase > 0 ? total / avgBase : null,
    ratePct: netSales > 0 ? (total / netSales) * 100 : 0,
    orders,
    staff,
    channels,
    pctDecimals,
});

const toChannelRows = (map: Map<string, number>, decimals: number): TgChannelRow[] => {
    const entries = Array.from(map.entries())
        .filter(([, amount]) => amount > 0)
        .sort(([, a], [, b]) => b - a);
    const pcts = distributeTg100(entries.map(([, amount]) => amount), decimals);
    return entries.map(([channel, amount], i) => ({ channel, amount, pct: pcts[i] }));
};

/**
 * Build both drill-in sections.
 *
 * Totals and order counts come from the report API when the surface has them
 * (`/sales/live/v2/snapshot-details` exposes `tip`, `gratuity`, `ordersWithTips`,
 * `ordersWithGratuity`, `orderCount`, `netSales`) — those are authoritative and keep the
 * drill-in in step with the card. The staff and channel arrays only drive the two rankings
 * and the two channel panels, which no single-row endpoint can provide. Surfaces without
 * those API figures (EOD) omit them and the staff buckets are summed as a fallback.
 */
export const buildTipsGratuityData = ({
    staff,
    channels,
    netSales,
    totalOrders,
    tipsTotal,
    gratuityTotal,
    ordersWithTips,
    ordersWithGratuity,
    includeGratuity = true,
    tipRefundsByCheckOwner,
}: {
    /** Staff × channel buckets — rankings only. */
    staff: TgStaffInput[];
    /** Channel buckets — channel panels only. */
    channels: TgChannelInput[];
    /** Rate denominator for BOTH sections. */
    netSales: number;
    /** Denominator for the Tips section's Avg/Order. Falls back to the staff buckets. */
    totalOrders?: number;
    tipsTotal?: number;
    gratuityTotal?: number;
    ordersWithTips?: number;
    /** Denominator for the Gratuity section's Avg/Gratuity Order. */
    ordersWithGratuity?: number;
    /** House-retained outlets: gratuity is revenue, never staff-bound — exclude it. */
    includeGratuity?: boolean;
    /**
     * REF-6 — tip/gratuity refunds keyed by check owner (refundModel's
     * `tipRefundsByCheckOwner`). `/sales/live/v2/staff-perfomance`'s own tip/gratuity sums
     * are already net of any tip/gratuity refund, so this is NOT subtracted from `amount`
     * (that would double-net it) — it only carries the after-tip-out exposure through to
     * the ranking row, for refunds that landed after that staff member's tip period had
     * already closed and been paid out.
     */
    tipRefundsByCheckOwner?: Record<string, { tips: number; gratuity: number; total: number; afterTipOut: number }>;
}): TipsGratuityData => {
    const tipTotal = tipsTotal ?? staff.reduce((s, r) => s + (r.tip || 0), 0);
    const gratTotal = !includeGratuity
        ? 0
        : gratuityTotal ?? staff.reduce((s, r) => s + (r.gratuity || 0), 0);
    const orderTotal = totalOrders ?? staff.reduce((s, r) => s + (r.orderCount || 0), 0);
    const tipOrders = ordersWithTips
        ?? staff.reduce((s, r) => s + ((r.tip || 0) > 0 ? (r.orderCount || 0) : 0), 0);
    const gratuityOrders = !includeGratuity
        ? 0
        : ordersWithGratuity
        ?? staff.reduce((s, r) => s + ((r.gratuity || 0) > 0 ? (r.orderCount || 0) : 0), 0);

    // Staff rankings — a name can appear in several buckets (one per channel), so fold
    // by name first. Sales are folded alongside so the individual tip rate is that staff
    // member's own tips ÷ own sales, not their share of the house total.
    const byStaff = new Map<string, { tip: number; gratuity: number; netSales: number }>();
    staff.forEach((row) => {
        const name = (row.name || '').trim() || 'Unknown';
        const acc = byStaff.get(name) ?? { tip: 0, gratuity: 0, netSales: 0 };
        acc.tip += row.tip || 0;
        acc.gratuity += row.gratuity || 0;
        acc.netSales += row.netSales || 0;
        byStaff.set(name, acc);
    });

    // REF-6 — staff-perfomance's own tip/gratuity sums are already net of any tip/gratuity
    // refund (the orders feeding it reflect the refund), so `amount` below is the gross
    // staff-perfomance figure as-is — NOT additionally reduced by tipRefundsByCheckOwner,
    // which would double-net it. That map only flags after-tip-out exposure now.
    const exposureFor = (name: string): number | undefined => {
        const exposed = tipRefundsByCheckOwner?.[name]?.afterTipOut ?? 0;
        return exposed > 0 ? exposed : undefined;
    };

    const tipStaff: TgStaffRow[] = Array.from(byStaff.entries())
        // Zero-tip staff stay in the ranking at "0.0%" — that is a coaching signal, not noise.
        // A staff member zeroed out this period but flagged with after-tip-out exposure also
        // stays, so the ranking has a row for the amber icon to attach to (REF-6).
        .filter(([name, v]) => v.tip > 0 || v.netSales > 0 || exposureFor(name) != null)
        .map(([name, v]) => ({
            name,
            amount: Number(v.tip.toFixed(2)),
            // Sales but no tips → 0.0%. Tips but no sales (pooled share) → incalculable.
            ratePct: v.netSales > 0 ? (v.tip / v.netSales) * 100 : null,
            refundedAfterTipOut: exposureFor(name),
        }))
        .sort((a, b) => b.amount - a.amount);

    const gratuityStaff: TgStaffRow[] = includeGratuity
        ? Array.from(byStaff.entries())
            .filter(([name, v]) => v.gratuity > 0 || exposureFor(name) != null)
            .map(([name, v]) => ({
                name,
                amount: Number(v.gratuity.toFixed(2)),
                // Gratuity is attributed to the check owner and is not pooled, so no rate column.
                ratePct: null,
                refundedAfterTipOut: exposureFor(name),
            }))
            .sort((a, b) => b.amount - a.amount)
        : [];

    // Both channel panels come from /sales/live/v2/by-channel (`channel` + `tip` / `gratuity`).
    // That endpoint is the only one with a real `channel`: staff-perfomance returns it as null
    // on every row, so it can attribute money to a person but never to a channel. The two
    // endpoints are exact mirrors — by-channel returns `staff_name: null` — which is why the
    // rankings stay on staff-perfomance and the channel panels stay here.
    const tipChannelMap = new Map<string, number>();
    const gratuityChannelMap = new Map<string, number>();
    channels.forEach((row) => {
        const canonical = getCanonicalTgChannel(row.channel);
        if ((row.tip || 0) > 0) tipChannelMap.set(canonical, (tipChannelMap.get(canonical) ?? 0) + row.tip);
        if (includeGratuity && (row.gratuity || 0) > 0) {
            gratuityChannelMap.set(canonical, (gratuityChannelMap.get(canonical) ?? 0) + row.gratuity);
        }
    });

    return {
        combined: tipTotal + gratTotal,
        // Tips: Avg/Order = Total Tips ÷ Total Orders. Channel percents at whole numbers.
        tips: buildSection(tipTotal, tipOrders, orderTotal, netSales, tipStaff, toChannelRows(tipChannelMap, 0), 0),
        // Gratuity: Avg/Gratuity Order = Total Gratuity ÷ gratuity-bearing orders (not all
        // orders). Channel percents at 2dp, still summing to exactly 100.00.
        gratuity: buildSection(gratTotal, gratuityOrders, gratuityOrders, netSales, gratuityStaff, toChannelRows(gratuityChannelMap, 2), 2),
    };
};

/** Drill-in sub-header: "$505.83 total · Tips $216.52 · Gratuity $289.31". */
export const tgSubHeader = (data: TipsGratuityData, fmt: (n: number) => string, includeGratuity = true): string =>
    includeGratuity
        ? `${fmt(data.combined)} total · Tips ${fmt(data.tips.total)} · Gratuity ${fmt(data.gratuity.total)}`
        : `${fmt(data.tips.total)} total · Tips`;
